import Constants from 'expo-constants';
import { Platform } from 'react-native';

let initialized = false;
let purchasesModulePromise: Promise<typeof import('react-native-purchases')> | null = null;
let initStatus: 'idle' | 'disabled' | 'missing_key' | 'invalid_test_key' | 'ready' | 'failed' = 'idle';

async function getPurchases() {
  if (process.env.JEST_WORKER_ID) {
    const mod = require('react-native-purchases');
    return mod.default;
  }
  if (!purchasesModulePromise) {
    purchasesModulePromise = import('react-native-purchases');
  }
  const mod = await purchasesModulePromise;
  return mod.default;
}

/**
 * RevenueCat is registered for the production App Store bundle only (`com.olamitoro.tripflow`).
 * Preview/dev/staging builds use a different bundle id, so the SDK is off unless explicitly forced
 * after you add a matching RevenueCat app / products.
 */
function isRevenueCatEnabledInThisBuild(): boolean {
  if (Platform.OS === 'web') return false;

  const flag = process.env.EXPO_PUBLIC_REVENUECAT_ENABLED?.trim().toLowerCase();
  if (flag === 'false' || flag === '0') return false;
  if (flag === 'true' || flag === '1') return true;

  const appEnv = (Constants.expoConfig?.extra as { appEnv?: string } | undefined)?.appEnv;
  return appEnv === 'production';
}

let didLogRevenueCatDisabled = false;

/** Single-flight init: concurrent callers (e.g. root layout + Subscribe tap) must not double-call configure(). */
let initWorkPromise: Promise<void> | null = null;

/** Set when Purchases.configure() throws or times out; cleared on success or before a new attempt. */
let lastConfigureError: string | undefined;

function rejectAfterMs(ms: number, label: string): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`));
    }, ms);
  });
}

const BILLING_CALL_TIMEOUT_MS = 12_000;
/** First configure can be slow on cold network / DNS; keep separate from other RC calls. */
const CONFIGURE_TIMEOUT_MS = 25_000;
const DEFAULT_PREMIUM_ENTITLEMENT_ID = 'tripvio_premium';

async function raceWithTimeout<T>(promise: Promise<T>, label: string, ms: number): Promise<T | null> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<null>((resolve) => {
    timeoutId = setTimeout(() => {
      console.warn(`[RevenueCat] ${label} timed out after ${ms}ms — continuing without billing`);
      resolve(null);
    }, ms);
  });
  const settled = promise.then(
    (v) => v as T | null,
    (err: unknown) => {
      console.warn(`[RevenueCat] ${label} failed`, err);
      return null;
    },
  );
  try {
    return await Promise.race([settled, timeoutPromise]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

function purchaseSdkErrorDetail(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message?: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return String(error);
}

/** RevenueCat CONFIGURATION_ERROR — products / offerings misconfigured vs the store dashboard. */
function isPurchasesConfigurationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const code = (error as { code?: unknown }).code;
  if (code === '23' || code === 23) return true;
  const message = typeof (error as { message?: unknown }).message === 'string'
    ? String((error as { message: string }).message).toLowerCase()
    : '';
  return (
    message.includes('none of the products registered in the revenuecat dashboard') ||
    (message.includes('could') &&
      message.includes('fetched') &&
      message.includes('app store connect'))
  );
}

type PurchasesOfferings = Awaited<ReturnType<Awaited<ReturnType<typeof getPurchases>>['getOfferings']>>;

/**
 * Resolve getOfferings with a single winner: success, error (so we can map Error 23), or timeout.
 */
function getOfferingsWithDeadline(
  fetchOfferings: () => Promise<PurchasesOfferings>,
  ms: number,
): Promise<
  | { status: 'ok'; offerings: PurchasesOfferings }
  | { status: 'error'; error: unknown }
  | { status: 'timeout' }
> {
  let settled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  return new Promise((resolve) => {
    timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve({ status: 'timeout' });
    }, ms);

    fetchOfferings()
      .then((offerings) => {
        if (settled) return;
        settled = true;
        if (timer !== undefined) clearTimeout(timer);
        resolve({ status: 'ok', offerings });
      })
      .catch((error: unknown) => {
        if (settled) return;
        settled = true;
        if (timer !== undefined) clearTimeout(timer);
        resolve({ status: 'error', error });
      });
  });
}

export type PresentRevenueCatPaywallResult =
  | { ok: true; outcome: 'purchased' | 'restored' | 'cancelled' | 'not_presented' | 'unknown'; detail?: string }
  | {
      ok: false;
      reason:
        | 'web'
        | 'disabled'
        | 'missing_key'
        | 'init_failed'
        | 'init_pending'
        | 'no_offerings'
        | 'present_failed';
      /** Short error text safe for logs/Sentry; may be empty */
      detail?: string;
    };

export type RevenueCatPremiumSnapshot = {
  isPremium: boolean;
  entitlementId?: string;
  expiresAt?: string;
  originalAppUserId?: string;
  currentAppUserId?: string;
};

function coerceRevenueCatAppUserId(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function readEmbeddedEnv(name: string): string | undefined {
  const fromProcess = process.env[name];
  if (typeof fromProcess === 'string' && fromProcess.trim().length > 0) {
    return fromProcess.trim();
  }
  const extra = Constants.expoConfig?.extra as Record<string, unknown> | undefined;
  const fromExtra = extra?.[name];
  if (typeof fromExtra === 'string' && fromExtra.trim().length > 0) {
    return fromExtra.trim();
  }
  return undefined;
}

/** RevenueCat "Test Store" keys must never ship in release/Play builds — the SDK force-quits the app. */
export function isRevenueCatTestStoreApiKey(key: string | undefined): boolean {
  return typeof key === 'string' && key.trim().startsWith('test_');
}

function resolveRevenueCatApiKey(): string | undefined {
  if (Platform.OS === 'web') return undefined;
  return Platform.OS === 'ios'
    ? readEmbeddedEnv('EXPO_PUBLIC_REVENUECAT_IOS_KEY')
    : readEmbeddedEnv('EXPO_PUBLIC_REVENUECAT_ANDROID_KEY');
}

function isReleaseLikeBuild(): boolean {
  if (__DEV__) return false;
  const appEnv = (Constants.expoConfig?.extra as { appEnv?: string } | undefined)?.appEnv;
  return appEnv === 'production';
}

/** Whether the Metro-inlined public SDK key is non-empty for the current platform (debugging only). */
export function isRevenueCatPublicSdkKeyEmbedded(): boolean {
  if (Platform.OS === 'web') return false;
  return Boolean(resolveRevenueCatApiKey());
}

function getPremiumEntitlementId(): string {
  return (
    process.env.EXPO_PUBLIC_REVENUECAT_PREMIUM_ENTITLEMENT_ID || DEFAULT_PREMIUM_ENTITLEMENT_ID
  ).trim() || DEFAULT_PREMIUM_ENTITLEMENT_ID;
}

/**
 * Presents the RevenueCat paywall (in-app subscription). No meaningful result on web.
 */
export async function presentRevenueCatPaywall(appUserId?: string): Promise<PresentRevenueCatPaywallResult> {
  if (Platform.OS === 'web') {
    return { ok: false, reason: 'web' };
  }
  await initRevenueCat();
  if (!initialized) {
    const { status, lastError } = getRevenueCatInitStatus();
    if (status === 'disabled') return { ok: false, reason: 'disabled' };
    if (status === 'missing_key') return { ok: false, reason: 'missing_key' };
    if (status === 'failed') return { ok: false, reason: 'init_failed', detail: lastError };
    return { ok: false, reason: 'init_pending' };
  }
  try {
    if (appUserId) {
      const loggedIn = await loginRevenueCatUser(appUserId);
      if (!loggedIn) {
        return {
          ok: false,
          reason: 'present_failed',
          detail: 'RevenueCat could not identify the current user before opening paywall',
        };
      }
    }
    const Purchases = await getPurchases();
    const offeringsResult = await getOfferingsWithDeadline(
      () => Purchases.getOfferings(),
      BILLING_CALL_TIMEOUT_MS,
    );

    if (offeringsResult.status === 'timeout') {
      console.warn(
        `[RevenueCat] Purchases.getOfferings timed out after ${BILLING_CALL_TIMEOUT_MS}ms`,
      );
      return {
        ok: false,
        reason: 'present_failed',
        detail: 'Offerings request timed out',
      };
    }

    if (offeringsResult.status === 'error') {
      const err = offeringsResult.error;
      if (isPurchasesConfigurationError(err)) {
        const detail = purchaseSdkErrorDetail(err);
        console.warn('[RevenueCat] Offerings configuration error (store / RevenueCat product setup)', err);
        return { ok: false, reason: 'no_offerings', detail };
      }
      const detail = purchaseSdkErrorDetail(err);
      console.warn('[RevenueCat] Purchases.getOfferings failed', err);
      return { ok: false, reason: 'present_failed', detail };
    }

    const offerings = offeringsResult.offerings;
    if (!offerings.current) {
      console.warn(
        '[RevenueCat] No current offering (RevenueCat dashboard → Product catalog → Offerings).',
      );
      return { ok: false, reason: 'no_offerings' };
    }

    const RevenueCatUI = await import('react-native-purchases-ui').then((m) => m.default);
    const rawPaywallResult = await RevenueCatUI.presentPaywall({ offering: offerings.current });
    const normalizedResult = String(rawPaywallResult || '').toLowerCase();
    if (normalizedResult.includes('purchased')) {
      return { ok: true, outcome: 'purchased', detail: String(rawPaywallResult || '') };
    }
    if (normalizedResult.includes('restored')) {
      return { ok: true, outcome: 'restored', detail: String(rawPaywallResult || '') };
    }
    if (normalizedResult.includes('cancel')) {
      return { ok: true, outcome: 'cancelled', detail: String(rawPaywallResult || '') };
    }
    if (normalizedResult.includes('not_presented')) {
      return { ok: true, outcome: 'not_presented', detail: String(rawPaywallResult || '') };
    }
    return { ok: true, outcome: 'unknown', detail: String(rawPaywallResult || '') };
  } catch (e) {
    if (isPurchasesConfigurationError(e)) {
      const detail = purchaseSdkErrorDetail(e);
      console.warn('[RevenueCat] Configuration error presenting paywall', e);
      return { ok: false, reason: 'no_offerings', detail };
    }
    const detail = purchaseSdkErrorDetail(e);
    console.warn('[RevenueCat] presentPaywall failed', e);
    return { ok: false, reason: 'present_failed', detail };
  }
}

async function performRevenueCatInit(): Promise<void> {
  if (initialized) {
    return;
  }

  lastConfigureError = undefined;

  if (!isRevenueCatEnabledInThisBuild()) {
    initStatus = 'disabled';
    if (!didLogRevenueCatDisabled && Platform.OS !== 'web') {
      didLogRevenueCatDisabled = true;
      console.warn(
        '[RevenueCat] Skipped: non-production build (see app.config.js extra.appEnv). ' +
          'Set EXPO_PUBLIC_REVENUECAT_ENABLED=true after configuring RC for this bundle.',
      );
    }
    return;
  }

  const apiKey = resolveRevenueCatApiKey();

  if (!apiKey) {
    console.warn('[RevenueCat] Missing SDK key');
    initStatus = 'missing_key';
    return;
  }

  if (isRevenueCatTestStoreApiKey(apiKey) && isReleaseLikeBuild()) {
    const msg =
      'Test Store API key cannot be used in production/release builds. Set EXPO_PUBLIC_REVENUECAT_ANDROID_KEY to the goog_… public key from RevenueCat (EAS secret), then republish OTA.';
    lastConfigureError = msg;
    console.warn(`[RevenueCat] ${msg}`);
    initStatus = 'invalid_test_key';
    return;
  }

  try {
    const Purchases = await getPurchases();
    await Promise.race([Purchases.configure({ apiKey }), rejectAfterMs(CONFIGURE_TIMEOUT_MS, 'Purchases.configure')]);
    initialized = true;
    initStatus = 'ready';
    lastConfigureError = undefined;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    lastConfigureError = msg;
    console.warn('[RevenueCat] Initialization failed', error);
    initStatus = 'failed';
  }
}

export async function initRevenueCat() {
  if (initialized) {
    return;
  }

  if (!initWorkPromise) {
    initWorkPromise = performRevenueCatInit().finally(() => {
      initWorkPromise = null;
    });
  }
  await initWorkPromise;
}

export function getRevenueCatInitStatus(): {
  initialized: boolean;
  status: 'idle' | 'disabled' | 'missing_key' | 'invalid_test_key' | 'ready' | 'failed';
  /** Present after status === 'failed' from configure() */
  lastError?: string;
} {
  return {
    initialized,
    status: initStatus,
    lastError: lastConfigureError,
  };
}

export async function loginRevenueCatUser(appUserId: string): Promise<boolean> {
  if (!appUserId) return false;
  await initRevenueCat();
  if (!initialized) return false;
  const Purchases = await getPurchases();
  const result = await raceWithTimeout(Purchases.logIn(appUserId), 'Purchases.logIn', BILLING_CALL_TIMEOUT_MS);
  if (result === null) return false;
  if (typeof Purchases.getAppUserID !== 'function') {
    console.warn('[RevenueCat] Unable to verify app user id after login');
    return false;
  }
  const currentAppUserId = await raceWithTimeout(
    Purchases.getAppUserID(),
    'Purchases.getAppUserID',
    BILLING_CALL_TIMEOUT_MS,
  );
  if (coerceRevenueCatAppUserId(currentAppUserId) !== appUserId) {
    console.warn('[RevenueCat] App user id mismatch after login', {
      expected: appUserId,
      actual: currentAppUserId,
    });
    return false;
  }
  return true;
}

export async function logoutRevenueCatUser() {
  if (!initialized) return;
  const Purchases = await getPurchases();
  await raceWithTimeout(Purchases.logOut(), 'Purchases.logOut', BILLING_CALL_TIMEOUT_MS);
}

/**
 * Attempt to restore/sync purchases from the app store into RevenueCat.
 * Returns true when a native restore call was executed.
 */
export async function restoreRevenueCatPurchases(appUserId?: string): Promise<boolean> {
  if (Platform.OS === 'web') {
    return false;
  }

  await initRevenueCat();
  if (!initialized) {
    console.warn('[RevenueCat] Restore skipped because SDK is not configured', getRevenueCatInitStatus());
    return false;
  }

  try {
    if (appUserId) {
      const loggedIn = await loginRevenueCatUser(appUserId);
      if (!loggedIn) return false;
    }
    const Purchases = await getPurchases();
    const restored = await raceWithTimeout(
      Purchases.restorePurchases().then(() => true as const),
      'Purchases.restorePurchases',
      BILLING_CALL_TIMEOUT_MS,
    );
    return !!restored;
  } catch (error) {
    try {
      const Purchases = await getPurchases();
      console.warn('[RevenueCat] restorePurchases failed, attempting syncPurchases fallback', error);
      const synced = await raceWithTimeout(
        Purchases.syncPurchases().then(() => true as const),
        'Purchases.syncPurchases',
        BILLING_CALL_TIMEOUT_MS,
      );
      return !!synced;
    } catch (syncError) {
      console.warn('[RevenueCat] syncPurchases fallback failed', syncError);
      return false;
    }
  }
}

/**
 * Read RevenueCat's on-device CustomerInfo cache (works offline after prior sync).
 * Configure entitlement id via EXPO_PUBLIC_REVENUECAT_PREMIUM_ENTITLEMENT_ID; defaults to "tripvio_premium".
 * Always false on web or when the SDK is not initialized.
 */
export async function getCachedPremiumFromRevenueCat(): Promise<boolean> {
  const snapshot = await getRevenueCatPremiumSnapshot();
  return snapshot.isPremium;
}

/**
 * Reads RevenueCat CustomerInfo from the device so the UI can reflect a successful purchase
 * before the backend webhook has propagated into Firestore.
 */
export async function getRevenueCatPremiumSnapshot(appUserId?: string): Promise<RevenueCatPremiumSnapshot> {
  if (Platform.OS === 'web') {
    return { isPremium: false };
  }
  await initRevenueCat();
  if (!initialized) {
    return { isPremium: false };
  }
  try {
    if (appUserId) {
      const loggedIn = await loginRevenueCatUser(appUserId);
      if (!loggedIn) return { isPremium: false };
    }
    const Purchases = await getPurchases();
    const currentAppUserIdRaw =
      typeof Purchases.getAppUserID === 'function'
        ? await raceWithTimeout(Purchases.getAppUserID(), 'Purchases.getAppUserID', BILLING_CALL_TIMEOUT_MS)
        : undefined;
    const currentAppUserId = coerceRevenueCatAppUserId(currentAppUserIdRaw);
    if (appUserId && currentAppUserId !== appUserId) {
      console.warn('[RevenueCat] Ignoring CustomerInfo for mismatched app user id', {
        expected: appUserId,
        actual: currentAppUserId,
      });
      return { isPremium: false, currentAppUserId };
    }
    const info = await raceWithTimeout(
      Purchases.getCustomerInfo(),
      'Purchases.getCustomerInfo',
      BILLING_CALL_TIMEOUT_MS,
    );
    if (!info) {
      return { isPremium: false };
    }
    const id = getPremiumEntitlementId();
    const customerInfo = info as {
      entitlements?: {
        active?: Record<
          string,
          {
            expirationDate?: string | null;
          }
        >;
      };
      originalAppUserId?: string;
    };
    const active = customerInfo.entitlements?.active || {};
    const entitlement = active[id] || (Object.keys(active).length === 1 ? active[Object.keys(active)[0]] : undefined);
    const entitlementId = active[id] ? id : Object.keys(active)[0];
    if (appUserId && entitlement && customerInfo.originalAppUserId && customerInfo.originalAppUserId !== appUserId) {
      console.warn('[RevenueCat] Ignoring active entitlement for different original app user id', {
        expected: appUserId,
        originalAppUserId: customerInfo.originalAppUserId,
        currentAppUserId,
        entitlementId,
      });
      return {
        isPremium: false,
        originalAppUserId: customerInfo.originalAppUserId,
        currentAppUserId,
      };
    }
    return {
      isPremium: Boolean(entitlement),
      entitlementId: entitlement ? entitlementId : undefined,
      expiresAt: entitlement?.expirationDate ?? undefined,
      originalAppUserId: customerInfo.originalAppUserId,
      currentAppUserId,
    };
  } catch (error) {
    if (__DEV__) {
      console.warn('[RevenueCat] getCachedPremiumFromRevenueCat failed', error);
    }
    return { isPremium: false };
  }
}